/**
 * Calculate the effective sale price for a product
 * Handles:
 * - Legacy sale_price field
 * - Percentage-based sales (sale_type: 'percentage')
 * - Fixed price sales (sale_type: 'fixed')
 * - Regional charm pricing (sale_type: 'regional') - different prices per currency
 * Also validates sale dates if they are set
 * 
 * @param {Object} product - The product object
 * @param {string} currency - Current currency code (USD, CAD, EUR, etc.) - optional, defaults to USD
 * @returns {Object} { isOnSale: boolean, effectivePrice: number|null, originalPrice: number, savings: number, savingsPercent: number, isCharmPricing: boolean }
 */
export const calculateSalePrice = (product, currency = 'USD') => {
  if (!product) {
    return { isOnSale: false, effectivePrice: null, originalPrice: 0, savings: 0, savingsPercent: 0, isCharmPricing: false };
  }

  let isOnSale = product.on_sale;
  let effectivePrice = null;
  let isCharmPricing = false;
  
  // Map currency codes - admin uses US/CA for region prices but USD/CAD for sale prices
  const regionCodeMap = { 'USD': 'US', 'CAD': 'CA', 'EUR': 'EUR' };
  const regionCode = regionCodeMap[currency] || currency;
  
  // Get the base/original price (MSRP) for the current currency
  let basePrice;
  
  // First try to get region-specific MSRP using the region code (US, CA)
  // Then try the currency code (USD, CAD) as fallback
  const regionMsrp = product.region_prices?.[regionCode] || product.region_prices?.[currency];
  
  if (regionMsrp && regionMsrp > 0) {
    basePrice = regionMsrp;
  } else {
    // Fall back to main price
    basePrice = product.price;
  }

  // Check if sale dates are valid (only if dates are actually set)
  if (isOnSale) {
    const now = new Date();
    
    // Only check start date if it's actually set
    if (product.sale_start_date && product.sale_start_date.trim && product.sale_start_date.trim() !== '') {
      const startDate = new Date(product.sale_start_date);
      if (!isNaN(startDate.getTime()) && startDate > now) {
        isOnSale = false;
      }
    }
    
    // Only check end date if it's actually set
    if (product.sale_end_date && product.sale_end_date.trim && product.sale_end_date.trim() !== '') {
      const endDate = new Date(product.sale_end_date);
      if (!isNaN(endDate.getTime()) && endDate < now) {
        isOnSale = false;
      }
    }
  }

  if (isOnSale) {
    // Check for regional charm pricing first (specific prices per currency)
    if (product.sale_type === 'regional' && product.regional_sale_prices) {
      const regionalPrice = product.regional_sale_prices[currency];
      if (regionalPrice && regionalPrice > 0) {
        effectivePrice = regionalPrice;
        isCharmPricing = true; // Flag that this is a fixed charm price, not a calculated price
      }
    }
    // Then check legacy sale_price field (direct price override)
    else if (product.sale_price && product.sale_price > 0) {
      effectivePrice = product.sale_price;
    }
    // Then check percentage-based sale
    else if (product.sale_type === 'percentage' && product.sale_value) {
      effectivePrice = basePrice * (1 - product.sale_value / 100);
    }
    // Finally check fixed price sale
    else if (product.sale_type === 'fixed' && product.sale_value) {
      effectivePrice = product.sale_value;
    }
  }

  // Calculate savings based on the currency-specific base price
  const savings = effectivePrice && effectivePrice < basePrice ? basePrice - effectivePrice : 0;
  const savingsPercent = effectivePrice && effectivePrice < basePrice 
    ? Math.round((savings / basePrice) * 100) 
    : 0;

  // Final check: sale price must actually be lower than original price
  const validSale = isOnSale && effectivePrice && effectivePrice < basePrice;

  return {
    isOnSale: validSale,
    effectivePrice: validSale ? effectivePrice : null,
    originalPrice: basePrice,
    savings,
    savingsPercent,
    isCharmPricing // If true, the price should be displayed as-is without currency conversion
  };
};

export default calculateSalePrice;
